import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Boxes, Search, History, CalendarClock, ArrowUpRight, ArrowDownRight,
  AlertTriangle, RefreshCcw, Target, Zap, Warehouse, Lock, Play, CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Modal, Button } from "../lib/ui";
import { fmtNum, fmtUnit, STOCK_STATUS } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const STATUS_TONE = { SAFE: "success", LOW: "warn", CRITICAL: "danger" };
const EXP_META = {
  EXPIRED: { tone: "danger", label: "Expired" },
  EXPIRING_7: { tone: "danger", label: "Exp ≤7d" },
  EXPIRING_30: { tone: "warn", label: "Exp ≤30d" },
  OK: { tone: "success", label: "Fresh" },
  NONE: { tone: "mute", label: "—" },
};
const FILTERS = [["ALL", "All"], ["CRITICAL", "Critical"], ["LOW", "Low"], ["SAFE", "Safe"]];

// Preparation-rule status labels (from Master Persiapan). Overrides the legacy min/ideal
// derivation for RTC rows so the RTC Inventory page becomes the single monitoring page
// that reflects the Owner's Target Standby & Minimum. Non-RTC rows keep legacy status.
const PREP_STATUS = {
  critical: { tone: "danger", label: "Kritis · Prepare Segera" },
  need_prepare: { tone: "warn", label: "Below Minimum · Prepare" },
  ready: { tone: "success", label: "Sudah Siap" },
};

export default function BranchInventory() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rows, setRows] = useState(null);       // null = loading, [] = empty, list = ok
  const [error, setError] = useState(null);
  const [queueMap, setQueueMap] = useState({}); // output_product_id -> queue item
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [moveFor, setMoveFor] = useState(null);
  const [moves, setMoves] = useState(null);

  // Branch selector for HQ users (Owner/SuperAdmin). Branch users are pinned by backend.
  const isHq = !user?.branch_id;
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");

  useEffect(() => {
    if (!isHq) return;
    api.get("/branches").then(({ data }) => {
      setBranches(data || []);
      setBranchId((cur) => cur || data?.[0]?.id || "");
    }).catch(() => setBranches([]));
  }, [isHq]);

  const activeBranch = useMemo(
    () => branches.find((b) => b.id === branchId) || (user?.branch_id ? { id: user.branch_id, name: user.branch_name } : null),
    [branches, branchId, user]);

  const load = useCallback(async () => {
    if (isHq && !branchId) { setRows([]); return; } // waiting for user to select a branch
    setError(null); setRows(null);
    const invParams = isHq && branchId ? { branch_id: branchId } : {};
    const queueParams = isHq && branchId ? { warehouse: branchId } : {};
    try {
      // Parallel: inventory + preparation queue (queue is best-effort — its failure never
      // blocks inventory display; we still surface all RTC stock).
      const [invRes, queueRes] = await Promise.all([
        api.get("/branch/my-inventory", { params: invParams }),
        api.get("/prepare/queue", { params: queueParams }).catch(() => ({ data: { items: [] } })),
      ]);
      setRows(Array.isArray(invRes.data) ? invRes.data : []);
      const qmap = {};
      for (const q of (queueRes.data?.items || [])) qmap[q.output_product_id] = q;
      setQueueMap(qmap);
    } catch (e) {
      setRows([]);
      setError(e.response?.data?.detail || t("Failed to load inventory"));
    }
  }, [isHq, branchId, t]);

  useEffect(() => { load(); }, [load]);

  const openMoves = async (r) => {
    setMoveFor(r); setMoves(null);
    const params = { product_id: r.product_id, ...(isHq && branchId ? { branch_id: branchId } : {}) };
    try {
      const { data } = await api.get("/branch/my-movements", { params });
      setMoves(Array.isArray(data) ? data : []);
    } catch {
      setMoves([]);
    }
  };

  const onPrepare = (r, q) => {
    // Reuse Meja Prepare wizard via router-state preset. No duplicate wizard, no duplicate inventory.
    navigate("/meja-prepare", {
      state: {
        preset: {
          source_product_id: q.source_product_id,
          output_product_id: r.product_id,
          suggested_qty: q.need_prepare,
        },
        warehouse_id: activeBranch?.id,
      },
    });
  };

  const renderBranchControl = () => (isHq ? (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 shadow-sm" data-testid="bi-branch-switcher">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-light text-primary"><Warehouse size={16} /></span>
      <div className="leading-tight">
        <p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Cabang")}</p>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="bi-branch-select"
          className="-ml-0.5 cursor-pointer bg-transparent text-sm font-bold text-ink focus:outline-none">
          <option value="">{t("— Pilih Cabang —")}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 shadow-sm" data-testid="bi-active-branch">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Warehouse size={16} /></span>
      <div className="leading-tight">
        <p className="flex items-center gap-1 text-[10px] font-semibold uppercase text-ink-mute"><Lock size={9} /> {t("Cabang")}</p>
        <p className="text-sm font-bold text-ink">{activeBranch?.name || user?.branch_name || "—"}</p>
      </div>
    </div>
  ));

  const Header = (
    <PageHeader title={t("My Branch Inventory")} subtitle={t("Your branch stock, movements & expiry")} icon={Boxes}>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={load} data-testid="bi-refresh"><RefreshCcw size={14} /> {t("Refresh")}</Button>
        {renderBranchControl()}
      </div>
    </PageHeader>
  );

  // --- render states (always terminate — no infinite spinner) ---
  if (isHq && !branchId && branches.length === 0) {
    // Branches still loading OR no branch access for this HQ account
    return <div className="animate-fade-up pb-24 lg:pb-2">{Header}<PageLoader /></div>;
  }
  if (isHq && !branchId) {
    return (
      <div className="animate-fade-up pb-24 lg:pb-2">
        {Header}
        <Card className="p-10 text-center" data-testid="bi-choose-branch">
          <Warehouse size={28} className="mx-auto mb-2 text-ink-mute" />
          <p className="text-sm text-ink-mute">{t("Pilih cabang untuk melihat Stok Bahan Siap Masak.")}</p>
        </Card>
      </div>
    );
  }
  if (rows === null) {
    return <div className="animate-fade-up pb-24 lg:pb-2">{Header}<PageLoader /></div>;
  }

  const filtered = rows.filter((r) =>
    (filter === "ALL" || r.status === filter) &&
    (r.product_name || "").toLowerCase().includes(search.toLowerCase()));
  const countFor = (f) => (f === "ALL" ? rows.length : rows.filter((r) => r.status === f).length);

  const bucketOf = (pc) => {
    const v = (pc || "").toUpperCase();
    if (v === "RTC") return "rtc";
    if (v === "SEMI_FINISHED" || v === "FINISHED") return "finished";
    return "raw";
  };
  const GROUPS = [
    ["raw", t("Raw Inventory"), "bg-amber-500"],
    ["rtc", t("Ready-to-Cook Inventory"), "bg-orange-500"],
    ["finished", t("Finished Products"), "bg-emerald-500"],
  ];

  // Kitchen Opening summary — computed from queue map for the CURRENT branch's RTC items.
  const openingItems = rows
    .filter((r) => bucketOf(r.production_class) === "rtc" && queueMap[r.product_id])
    .map((r) => ({ ...queueMap[r.product_id], product_name: r.product_name, unit: r.unit }))
    .filter((q) => q.status !== "ready");
  openingItems.sort((a, b) => {
    const rank = { critical: 0, need_prepare: 1, ready: 2 };
    return (rank[a.status] ?? 3) - (rank[b.status] ?? 3)
      || (b.need_prepare - a.need_prepare)
      || (a.product_name || "").localeCompare(b.product_name || "");
  });

  const renderCard = (r) => {
    const legacy = STOCK_STATUS[r.status] || { label: r.status };
    const exp = EXP_META[r.expiry_state] || EXP_META.NONE;
    const isRtc = bucketOf(r.production_class) === "rtc";
    const q = isRtc ? queueMap[r.product_id] : null;
    // Preparation-rule status wins for RTC rows when a rule is active (Target defined).
    const usePrepRule = !!q;
    const prepMeta = usePrepRule ? PREP_STATUS[q.status] : null;
    const target = q?.target_qty ?? r.ideal_stock;
    const minVal = q?.min_qty ?? r.minimum_stock;
    const need = q?.need_prepare ?? 0;
    const pct = target > 0 ? Math.min(100, Math.round((r.current_stock / target) * 100)) : 0;
    const barTone = usePrepRule
      ? (q.status === "critical" ? "bg-danger" : q.status === "need_prepare" ? "bg-warn" : "bg-success")
      : (r.status === "CRITICAL" ? "bg-danger" : r.status === "LOW" ? "bg-warn" : "bg-success");
    return (
      <Card key={r.product_id} className="p-4" data-testid={`branch-inv-${r.product_name}`}>
        <div className="flex items-start gap-3">
          <ProductAvatar image={r.image} colorKey={r.category_color} name={r.product_name} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading font-bold text-ink">{r.product_name}</p>
            <p className="text-[11px] font-bold uppercase tracking-wide text-ink-mute">{r.category_name}</p>
          </div>
          {usePrepRule ? (
            <Badge tone={prepMeta.tone} data-testid={`branch-inv-prep-status-${r.product_name}`}>{prepMeta.label}</Badge>
          ) : (
            <Badge tone={STATUS_TONE[r.status] || "mute"}>{t(legacy.label)}</Badge>
          )}
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="font-heading text-2xl font-bold text-ink">{fmtNum(r.current_stock)} <span className="text-xs font-bold uppercase text-ink-mute">{fmtUnit(r.unit)}</span></p>
            <p className="text-[11px] text-ink-mute">
              {usePrepRule ? (
                <>
                  <span data-testid={`branch-inv-target-${r.product_name}`}>{t("Target Standby")} <b>{fmtNum(target)}</b></span>
                  {" · "}
                  <span data-testid={`branch-inv-min-${r.product_name}`}>{t("Min")} <b>{fmtNum(minVal)}</b></span>
                </>
              ) : (
                <>{t("Min")} {fmtNum(r.minimum_stock)} · {t("Ideal")} {fmtNum(r.ideal_stock)}</>
              )}
            </p>
          </div>
          {r.expiry_state !== "NONE" && <Badge tone={exp.tone}><CalendarClock size={11} /> {t(exp.label)}</Badge>}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
          <div className={`h-full rounded-full ${barTone}`} style={{ width: `${pct}%` }} />
        </div>
        {usePrepRule && q.status !== "ready" && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-orange-200 bg-orange-50/60 px-2.5 py-1.5" data-testid={`branch-inv-need-${r.product_name}`}>
            <div className="min-w-0 text-xs">
              <p className="font-bold text-orange-800">{t("Perlu Prepare")}</p>
              <p className="text-[11px] text-orange-700">
                <span className={q.status === "critical" ? "font-extrabold" : "font-bold"}>{fmtNum(need)} {r.unit}</span>
                {" · "}dari <b>{q.source_name}</b>
              </p>
            </div>
            <Button size="sm" disabled={!q.source_current_stock || q.source_current_stock <= 0}
              onClick={() => onPrepare(r, q)} data-testid={`branch-inv-prepare-${r.product_name}`}>
              <Play size={13} /> {t("Prepare")}
            </Button>
          </div>
        )}
        <button onClick={() => openMoves(r)} data-testid={`branch-inv-history-${r.product_name}`}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-line py-1.5 text-xs font-semibold text-ink-soft transition-colors hover:bg-stone-50">
          <History size={14} /> {t("Movement History")}
        </button>
      </Card>
    );
  };

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      {Header}

      {error && (
        <Card className="mb-4 flex items-start gap-3 border-danger-light bg-danger-light/50 p-4" data-testid="bi-error">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-danger-text" />
          <div className="flex-1">
            <p className="font-bold text-danger-text">{t("Gagal memuat inventory")}</p>
            <p className="text-xs text-danger-text/80">{error}</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="bi-error-retry"><RefreshCcw size={13} /> {t("Coba Lagi")}</Button>
        </Card>
      )}

      {/* Kitchen Opening — Preparation Intelligence summary (Phase 1) */}
      {openingItems.length > 0 && (
        <Card className="mb-4 border-orange-200 bg-orange-50/40 p-4" data-testid="bi-opening-check">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-600"><Zap size={17} /></span>
            <div className="flex-1">
              <h3 className="font-heading text-base font-extrabold text-ink">{t("Kitchen Opening — Perlu Prepare")}</h3>
              <p className="text-[11px] text-ink-mute">{t("Otomatis dihitung dari Target Standby di Master Persiapan · Ordering: Kritis → gap terbesar → alfabetis")}</p>
            </div>
            <Badge tone="warn" data-testid="bi-opening-count"><Target size={11} /> {openingItems.length} item</Badge>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {openingItems.slice(0, 6).map((q) => {
              const meta = PREP_STATUS[q.status];
              return (
                <div key={q.output_product_id} className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2" data-testid={`bi-opening-item-${q.product_name}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-ink">{q.product_name}</p>
                    <p className="text-[11px]"><Badge tone={meta.tone} className="!py-0 !text-[9px]">{meta.label}</Badge> · <b>{fmtNum(q.need_prepare)} {q.unit}</b></p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onPrepare({ product_id: q.output_product_id, product_name: q.product_name, unit: q.unit }, q)} data-testid={`bi-opening-prepare-${q.product_name}`}>
                    <Play size={12} /> {t("Prepare")}
                  </Button>
                </div>
              );
            })}
          </div>
          {openingItems.length > 6 && <p className="mt-2 text-[11px] text-ink-mute">+{openingItems.length - 6} {t("item lain di daftar Bahan Siap Masak di bawah")}</p>}
        </Card>
      )}

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search size={18} className="absolute left-3.5 top-3 text-ink-mute" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Search products...")} className="cf-input pl-10" data-testid="branch-inv-search" />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="branch-inv-filters">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`branch-inv-filter-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all ${filter === k ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
              {t(label)}<span className={`rounded-full px-1.5 text-[11px] ${filter === k ? "bg-white/25" : "bg-stone-100 text-ink-mute"}`}>{countFor(k)}</span>
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 && !error ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="bi-empty">
          <Boxes size={28} className="mx-auto mb-2 text-ink-mute" />
          <p className="text-sm">{t("Belum ada inventory untuk cabang ini.")}</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="branch-inv-empty">{t("No products match.")}</Card>
      ) : (
        <div className="space-y-6">
          {GROUPS.map(([key, label, dot]) => {
            const items = filtered.filter((r) => bucketOf(r.production_class) === key);
            if (items.length === 0) return null;
            return (
              <div key={key} data-testid={`branch-inv-group-${key}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
                  <h3 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">{label}</h3>
                  <span className="rounded-full bg-stone-100 px-2 text-[11px] font-bold text-ink-mute">{items.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {items.map(renderCard)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!moveFor} onClose={() => setMoveFor(null)} title={`${moveFor?.product_name || ""} · ${t("History")}`} size="md">
        {!moves ? (
          <p className="py-8 text-center text-sm text-ink-mute">{t("Loading...")}</p>
        ) : moves.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-mute">{t("No movements recorded yet.")}</p>
        ) : (
          <div className="space-y-2" data-testid="branch-movements">
            {moves.map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-xl border border-line px-3 py-2.5">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.direction === "in" ? "bg-success-light text-success-text" : "bg-danger-light text-danger-text"}`}>
                  {m.direction === "in" ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">{m.type.replace(/_/g, " ")}</p>
                  <p className="truncate text-[11px] text-ink-mute">{m.reason || m.reference_no} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${m.direction === "in" ? "text-success-text" : "text-danger-text"}`}>{m.direction === "in" ? "+" : "-"}{fmtNum(m.quantity)}</p>
                  <p className="text-[11px] text-ink-mute">→ {fmtNum(m.balance_after)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
